import { useQuery } from '@tanstack/react-query';
import { PaginatedResource } from '../../typings/Paginated';
import { BaseService, GenericPaginatedParameters, Unauthorized, UnexpectedError } from './base-service';

interface SessionValidityTimeParams {
    /**
     * Session validity time in seconds.
     */
    sessionValidityTime: number;
    sessionQuantityLimit: number;
    /**
     * Session validity time in seconds.
     */
    mobileSessionValidityTime: number;
    mobileSessionQuantityLimit: number;
}

interface MapLimits {
    occurrenceMapPolygon: string | null;
    thirdPartyCameraMapPolygon: string | null;
    occurrenceAreaNameMapPolygon: string | null;
}

interface EmailRecipients {
    abuseReportEmailRecipient: string | null;
    thirdPartyInscriptionEmailRecipient: string | null;
}

interface LoginAttemptLimits {
    allowedLoginAttempts: number;
    blockedLoginWaitTime: number;
}

class PlatformSettingsService extends BaseService {
    async updateTermsOfUse({ termsOfUse }: { termsOfUse: string; }): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/terms-of-use`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                termsOfUse
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getSession(): Promise<SessionValidityTimeParams> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/sessions`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateSession({ sessionValidityTime, sessionQuantityLimit, mobileSessionQuantityLimit, mobileSessionValidityTime }: SessionValidityTimeParams): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/sessions`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                sessionValidityTime,
                sessionQuantityLimit,
                mobileSessionQuantityLimit,
                mobileSessionValidityTime,
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async updateMapLimits(occurrenceMapPolygon: string | null, thirdPartyCameraMapPolygon: string | null, occurrenceAreaNameMapPolygon: string | null): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/map-limits`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                occurrenceMapPolygon,
                thirdPartyCameraMapPolygon,
                occurrenceAreaNameMapPolygon,
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getMapLimits(): Promise<MapLimits> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/map-limits`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPaginatedEventsIntegration(params: GenericPaginatedParameters): Promise<PaginatedResource<EventsIntegration>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/platform-settings/events-integration/paginated${this.encodeQueryParams({
            limit: params.limit,
            page: params.page,
            textFilter: params.textFilter,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateEventIntegration(eventIntegration: { id: number; actingBodyId: number | null; occurrenceTypeId: number | null; }): Promise<EventsIntegration> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/platform-settings/events-integration/${eventIntegration.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                actingBodyId: eventIntegration.actingBodyId,
                occurrenceTypeId: eventIntegration.occurrenceTypeId
            })
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateFrequentlyAskedQuestions({ frequentlyAskedQuestions }: { frequentlyAskedQuestions: FrequentlyAskedQuestion[] | null; }): Promise<void> {
        const token = await this.getToken();

        const formData = new FormData();
        formData.append('frequentlyAskedQuestions', JSON.stringify(frequentlyAskedQuestions));
        frequentlyAskedQuestions?.forEach((faq) => {
            if (faq.file) {
                formData.append(`frequentlyAskedQuestions_${faq.id}`, faq.file);
            }
        });

        const res = await fetch(`${this.centralEndpoint}/platform-settings/frequently-asked-questions`, {
            method: 'PATCH',
            headers: this.getHeaders(token),
            body: formData
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getFrequentlyAskedQuestions(): Promise<FrequentlyAskedQuestion[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/frequently-asked-questions`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    private async getMapCentralPoint(): Promise<{ latitude: number; longitude: number; zoom: number; radius: number; }> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/central-map-point`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    useMapCentralPoint() {
        return useQuery({
            queryKey: ['getMapCentralPoint'],
            queryFn: () => this.getMapCentralPoint(),
            staleTime: Infinity,
            cacheTime: Infinity,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            enabled: true,

        });
    }

    async getEmailRecipients(): Promise<EmailRecipients> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/email-recipients`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateEmailRecipients(params: EmailRecipients) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/email-recipients`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getLoginAttemptLimits(): Promise<LoginAttemptLimits> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/login-attempt-limits`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateLoginAttemptLimits(params: LoginAttemptLimits) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/platform-settings/login-attempt-limits`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }
}

export const platformSettingsService = new PlatformSettingsService();
